// Read-only admin views of ONE user's schedules, devices and rooms.
//
// Two rules hold across this file:
//
//  1. Everything goes through axiosInstance. Raw axios/fetch bypasses the auth
//     interceptor, so those callers never get a token refresh — that is why
//     Dashboard, ScheduleHistory, UsageMonitoring and Setting silently log the
//     user out on an expired token. New code does not repeat it.
//
//  2. No toasts on read failures. A failed read is rendered as an ErrorState in
//     the table it belongs to, with a Retry button, so the user can see WHICH
//     panel failed. A toast floats free of the thing that broke and disappears.
//     Errors are re-thrown for the caller's state machine to catch.

import axiosInstance from "../api/axiosInstance";
import { ADMIN_USER_DATA_API } from "../api/apiroutes";

/**
 * Normalise the envelope. The backend returns
 * `{ code, success, data: {...}, pagination }`, but some older admin handlers
 * omit `pagination` entirely — default it rather than letting `meta.page`
 * explode in a component.
 */
function unwrap(res, key) {
  const body = res?.data ?? {};
  const payload = body.data ?? {};
  const items = key ? (payload[key] ?? []) : payload;

  return {
    items,
    user: payload.user ?? null,
    summary: body.summary ?? null,
    isAddedScope: body.isAddedScope ?? null,
    pagination: body.pagination ?? {
      page: 1,
      limit: Array.isArray(items) ? items.length : 0,
      total: Array.isArray(items) ? items.length : 0,
      totalPages: 1,
    },
  };
}

/** Drop empty/`"all"` filters so they never reach the server as literals. */
function cleanParams(params = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === "all") continue;
    out[k] = v;
  }
  return out;
}

export const adminUserService = {
  async fetchUserSchedules(userId, params = {}) {
    const res = await axiosInstance.get(ADMIN_USER_DATA_API.SCHEDULES(userId), {
      params: cleanParams(params),
    });
    return unwrap(res, "schedules");
  },

  async fetchUserSchedule(userId, scheduleId) {
    const res = await axiosInstance.get(
      ADMIN_USER_DATA_API.SCHEDULE_BY_ID(userId, scheduleId)
    );
    return res?.data?.data ?? null;
  },

  async fetchUserDevices(userId, params = {}) {
    const res = await axiosInstance.get(ADMIN_USER_DATA_API.DEVICES(userId), {
      params: cleanParams(params),
    });
    return unwrap(res, "devices");
  },

  async fetchUserRooms(userId, params = {}) {
    const res = await axiosInstance.get(ADMIN_USER_DATA_API.ROOMS(userId), {
      params: cleanParams(params),
    });
    return unwrap(res, "rooms");
  },

  async fetchRoomDetail(userId, roomId, params = {}) {
    const res = await axiosInstance.get(
      ADMIN_USER_DATA_API.ROOM_BY_ID(userId, roomId),
      { params: cleanParams(params) }
    );
    const body = res?.data ?? {};
    return {
      room: body.data?.room ?? null,
      devices: body.data?.devices ?? [],
      pagination: body.pagination ?? { page: 1, limit: 0, total: 0, totalPages: 1 },
    };
  },

  async fetchUserNextRuns(userId, params = {}) {
    const res = await axiosInstance.get(ADMIN_USER_DATA_API.NEXT_RUNS(userId), {
      params: cleanParams(params),
    });
    return unwrap(res, "items");
  },

  async fetchUserStats(userId) {
    const res = await axiosInstance.post(ADMIN_USER_DATA_API.USER_STATS, {
      userId,
    });
    return res?.data?.data ?? null;
  },

  /**
   * The full device list for one user, reduced to the lookup every cross-module
   * link needs.
   *
   * ⚠️ `byVendorId` is keyed on `Device.deviceId` — the VENDOR id string that
   * `Schedule.actions[].deviceId` stores. `byId` is keyed on `Device._id`, the
   * Mongo id that device routes take. Mixing them up is the single easiest way
   * to produce a dead link here.
   */
  async fetchDeviceIndex(userId) {
    // ⚠️ Must page. The server caps `limit` at 100, so a single request would
    // silently truncate the index for a user with more devices than that — and
    // every unresolved entry degrades to an un-clickable raw vendor id, which
    // looks like missing data rather than a truncated fetch.
    // MAX_PAGES bounds it so a bad `totalPages` cannot spin forever.
    const MAX_PAGES = 20;
    const LIMIT = 100;

    const all = [];
    let page = 1;
    let totalPages = 1;

    do {
      const res = await this.fetchUserDevices(userId, {
        page,
        limit: LIMIT,
        includeRemoved: true,
      });
      all.push(...(res.items || []));
      totalPages = res.pagination?.totalPages ?? 1;
      page += 1;
    } while (page <= totalPages && page <= MAX_PAGES);

    const byVendorId = {};
    const byId = {};
    for (const device of all) {
      if (device.deviceId) byVendorId[device.deviceId] = device;
      if (device._id) byId[device._id] = device;
    }
    return { byVendorId, byId, all };
  },
};

export default adminUserService;
